// <s-product-gallery> — one main stage (scroll-snap track) plus a thumbnail row; thumbnails, arrows, swipe and variant changes stay in sync.
class SProductGallery extends HTMLElement {
  #controller;
  #track;
  #slides = [];
  #thumbs = [];
  #index = 0;
  #observer;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = [...this.#track.children];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];

    this.addEventListener('command', (event) => {
      if (event.command === '--next') this.go(this.#index + 1);
      else if (event.command === '--prev') this.go(this.#index - 1);
      else if (event.command === '--goto') this.go(Number(event.source?.dataset.index ?? 0));
    }, { signal });

    // Swipes update the active thumbnail once a slide is mostly in view.
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          this.#setActive(this.#slides.indexOf(entry.target));
        }
      }
    }, { root: this.#track, threshold: [0.6] });
    this.#slides.forEach((slide) => this.#observer.observe(slide));

    // Picking a color jumps to that variant's image when it has one.
    const map = this.#readMap();
    const form = document.querySelector('[data-product-form]');
    form?.addEventListener('change', (event) => {
      const id = event.target?.dataset?.variantId;
      if (!id || map[id] === undefined) return;
      this.go(map[id]);
    }, { signal });

    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  go(index) {
    const count = this.#slides.length;
    if (!count) return;
    const target = (index + count) % count;
    const slide = this.#slides[target];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left: slide.offsetLeft - this.#track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(target);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    this.#slides.forEach((slide, i) => {
      const active = i === index;
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
      if (!active) slide.querySelector('video')?.pause();
    });
    this.#thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const thumb = this.#thumbs[index];
    const row = thumb?.closest('[data-gallery-thumbs]');
    if (thumb && row) {
      const left = thumb.offsetLeft - row.clientWidth / 2 + thumb.clientWidth / 2;
      row.scrollTo({ left, behavior: 'auto' });
    }
  }

  #readMap() {
    try {
      return JSON.parse(this.querySelector('[data-variant-media-map]')?.textContent || '{}');
    } catch {
      return {};
    }
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
